import { LegalLayout } from "../components/legal-layout.js";

const SECTIONS = [
  { id: "acceptance", label: "Acceptance" },
  { id: "service", label: "The service" },
  { id: "accounts", label: "Accounts" },
  { id: "content", label: "Your content" },
  { id: "conduct", label: "Acceptable use" },
  { id: "fees", label: "Fees" },
  { id: "termination", label: "Termination" },
  { id: "disclaimers", label: "Disclaimers" },
  { id: "liability", label: "Liability" },
  { id: "law", label: "Governing law" },
  { id: "changes", label: "Changes" },
  { id: "contact", label: "Contact" },
] as const;

export function Terms() {
  return (
    <LegalLayout title="Terms of Service" updated="August 21, 2026" sections={SECTIONS}>
      <section id="acceptance" className="docs2__section">
        <h2>Acceptance</h2>
        <p>
          These terms are a contract between you and{" "}
          <strong>Routing Run Private Limited</strong>, a company incorporated in India
          (&ldquo;saucer&rdquo;, &ldquo;we&rdquo;, &ldquo;us&rdquo;), governing your use of
          saucer.sh. By creating an account or otherwise using the service, you agree to them.
          If you do not agree, do not use saucer.sh.
        </p>
      </section>

      <section id="service" className="docs2__section">
        <h2>The service</h2>
        <p>
          saucer.sh turns files you upload into links under your account name, with optional
          image transforms, passwords, private links, search and an AI assistant, as described
          in the <a href="/docs">docs</a>. Anyone with a GitHub account may sign up, and features may
          change as the product evolves.
        </p>
      </section>

      <section id="accounts" className="docs2__section">
        <h2>Accounts</h2>
        <p>
          You sign in with GitHub OAuth; your GitHub username becomes your namespace on
          saucer.sh. You are responsible for activity under your account and for keeping any
          CLI tokens you mint confidential. Tell us immediately if you suspect a token or
          session has been compromised so we can revoke it.
        </p>
      </section>

      <section id="content" className="docs2__section">
        <h2>Your content</h2>
        <p>
          You keep ownership of everything you upload. You grant us the limited license needed
          to store, process, and serve it back to you and to whoever you share a link with —
          including generating thumbnails, social cards, and AI-derived titles/descriptions for
          search. You are solely responsible for what you upload and for having the rights to
          share it.
        </p>
      </section>

      <section id="conduct" className="docs2__section">
        <h2>Acceptable use</h2>
        <p>You may not use saucer.sh to:</p>
        <div className="cmdrows">
          <div className="cmdrows__row">
            <code className="mono">Infringe</code>
            <span>Upload or distribute content that infringes copyright, trademark, or other IP rights.</span>
          </div>
          <div className="cmdrows__row">
            <code className="mono">Abuse</code>
            <span>Upload malware, phishing pages, or content that violates applicable law.</span>
          </div>
          <div className="cmdrows__row">
            <code className="mono">Overload</code>
            <span>Attempt to circumvent rate limits, storage limits, or interfere with the service&rsquo;s operation.</span>
          </div>
          <div className="cmdrows__row">
            <code className="mono">Exploit</code>
            <span>Probe, scan, or bypass access controls, or resell access without our consent.</span>
          </div>
        </div>
        <p>
          We may remove content and suspend or terminate accounts that violate this section,
          including in response to a valid notice under our <a href="/dmca">DMCA policy</a>.
        </p>
      </section>

      <section id="fees" className="docs2__section">
        <h2>Fees</h2>
        <p>
          saucer.sh has a free plan and paid plans, described on the{" "}
          <a href="/pricing">pricing page</a>. Paid plans are arranged and applied manually — we
          do not collect or store card details, and no charge is ever taken automatically. If an
          account stops paying it returns to the free plan: nothing is deleted and existing links
          keep working, but new uploads are refused while the account is over the free quota. We
          will give advance notice before changing the price or the allowance of an existing
          paid account.
        </p>
      </section>

      <section id="termination" className="docs2__section">
        <h2>Termination</h2>
        <p>
          You may stop using saucer.sh and delete your content at any time. We may suspend or
          terminate an account that violates these terms, poses a security risk, or as required
          by law. Where reasonably possible we will give notice first; for abuse, security, or
          legal reasons we may act immediately.
        </p>
      </section>

      <section id="disclaimers" className="docs2__section">
        <h2>Disclaimers</h2>
        <p>
          saucer.sh is provided &ldquo;as is&rdquo; and &ldquo;as available,&rdquo; without
          warranties of any kind, express or implied, including merchantability, fitness for a
          particular purpose, and non-infringement. We do not warrant the service will be
          uninterrupted or error-free.
        </p>
      </section>

      <section id="liability" className="docs2__section">
        <h2>Liability</h2>
        <p>
          To the maximum extent permitted by law, Routing Run Private Limited will not be
          liable for indirect, incidental, special, or consequential damages arising from your
          use of saucer.sh, and our total liability for any claim will not exceed the amount
          you paid us, if any, in the twelve months before the claim arose.
        </p>
      </section>

      <section id="law" className="docs2__section">
        <h2>Governing law</h2>
        <p>
          These terms are governed by the laws of India. Any dispute arising from them will be
          subject to the exclusive jurisdiction of the courts of Bengaluru, Karnataka, India.
        </p>
      </section>

      <section id="changes" className="docs2__section">
        <h2>Changes</h2>
        <p>
          We may update these terms as the product changes. Continuing to use saucer.sh after
          an update means you accept the revised terms; material changes will be reflected by
          updating the date at the top of this page.
        </p>
      </section>

      <section id="contact" className="docs2__section">
        <h2>Contact</h2>
        <p>
          Questions about these terms: <a href="mailto:email@saucer.sh">email@saucer.sh</a>.
        </p>
      </section>
    </LegalLayout>
  );
}
